/**
 * modals/ConfluencePicker.tsx
 * Multi-select picker for confluences with polarity assignment
 *
 * Shows available confluences grouped by category.
 * Allows multi-select, polarity toggle (positive/negative), and inline creation.
 */

// 1. External dependencies
import React, { useState, useEffect, useMemo } from 'react';
import { Plus, AlertTriangle, MoreVertical, Pencil, Trash2, ThumbsUp, ThumbsDown } from 'lucide-react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Listbox,
  ListboxItem,
  ListboxSection,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  ButtonGroup,
  Chip
} from '@enfomentis/ui';

// 3. Relative imports
import type { ConfluenceDraft, MarketConditionCategoryDraft, PlaybookDraft, PlaybookConfluenceLinkDraft } from '../hooks/usePlaybookBuilder';
import { ConfluenceModal } from './ConfluenceModal';
import { DeleteConfluenceConfirmationModal } from './DeleteConfluenceConfirmationModal';

/**
 * Props for ConfluencePicker
 */
interface ConfluencePickerProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Available confluences */
  confluences: ConfluenceDraft[];
  /** Market condition categories */
  marketConditionCategories: MarketConditionCategoryDraft[];
  /** Currently selected confluences with polarity */
  selectedConfluences: PlaybookConfluenceLinkDraft[];
  /** All playbooks (for delete confirmation) */
  playbooks: PlaybookDraft[];
  /** Current playbook ID (for delete confirmation) */
  currentPlaybookId: string;
  /** Handler for selection change - receives confluence links with polarity */
  onSelectionChange: (selectedConfluences: PlaybookConfluenceLinkDraft[]) => void;
  /** Handler for adding new confluence */
  onAddConfluence: (confluenceData: { name: string; description?: string; categoryId?: string }) => string;
  /** Handler for creating a new category */
  onCreateCategory: (name: string, color: string, description?: string) => string;
  /** Handler for updating a confluence */
  onUpdateConfluence: (tempId: string, updates: { name?: string; description?: string; categoryId?: string }) => void;
  /** Handler for removing a confluence */
  onRemoveConfluence: (tempId: string) => void;
}

/**
 * Empty state component
 */
const EmptyState: React.FC<{ onAddNew: () => void }> = ({ onAddNew }) => (
  <div className="flex-1 flex items-center justify-center py-12">
    <div className="text-center max-w-md">
      <AlertTriangle className="w-16 h-16 text-text-secondary mx-auto mb-4" />
      <h4 className="text-lg font-medium text-text-primary mb-2">No Confluences Yet</h4>
      <p className="text-sm text-text-secondary mb-4">
        Create your first confluence to define market factors for your playbook.
      </p>
      <Button
        color="primary"
        startContent={<Plus className="w-4 h-4" />}
        onPress={onAddNew}
      >
        Create First Confluence
      </Button>
    </div>
  </div>
);

/**
 * Polarity toggle component for selected confluences
 */
const PolarityToggle: React.FC<{
  polarity: 'positive' | 'negative';
  onToggle: (polarity: 'positive' | 'negative') => void;
}> = ({ polarity, onToggle }) => (
  <ButtonGroup size="sm" className="ml-2">
    <Button
      isIconOnly
      variant={polarity === 'positive' ? 'solid' : 'light'}
      color={polarity === 'positive' ? 'success' : 'default'}
      onPress={() => onToggle('positive')}
      aria-label="Set as positive confluence"
    >
      <ThumbsUp className="w-3 h-3" />
    </Button>
    <Button
      isIconOnly
      variant={polarity === 'negative' ? 'solid' : 'light'}
      color={polarity === 'negative' ? 'warning' : 'default'}
      onPress={() => onToggle('negative')}
      aria-label="Set as negative confluence"
    >
      <ThumbsDown className="w-3 h-3" />
    </Button>
  </ButtonGroup>
);

/**
 * Confluence picker component
 */
export const ConfluencePicker: React.FC<ConfluencePickerProps> = ({
  isOpen,
  onClose,
  confluences,
  marketConditionCategories,
  selectedConfluences,
  playbooks,
  currentPlaybookId,
  onSelectionChange,
  onAddConfluence,
  onCreateCategory,
  onUpdateConfluence,
  onRemoveConfluence
}) => {
  // Track selected confluence IDs for the Listbox
  const [localSelectedKeys, setLocalSelectedKeys] = useState<Set<string>>(
    new Set(selectedConfluences.map(c => c.confluenceId))
  );

  // Track polarity for each selected confluence
  const [localPolarities, setLocalPolarities] = useState<Map<string, 'positive' | 'negative'>>(
    new Map(selectedConfluences.map(c => [c.confluenceId, c.polarity]))
  );

  const [showConfluenceModal, setShowConfluenceModal] = useState(false);
  const [editingConfluence, setEditingConfluence] = useState<ConfluenceDraft | undefined>(undefined);
  const [deletingConfluenceId, setDeletingConfluenceId] = useState<string | undefined>(undefined);

  // Update local state when props change
  useEffect(() => {
    setLocalSelectedKeys(new Set(selectedConfluences.map(c => c.confluenceId)));
    setLocalPolarities(new Map(selectedConfluences.map(c => [c.confluenceId, c.polarity])));
  }, [selectedConfluences, isOpen]);

  /**
   * Handle selection change from Listbox
   */
  const handleSelectionChange = (keys: Set<string>): void => {
    const newKeys = keys as Set<string>;

    // For newly selected items, default to 'negative' polarity
    const newPolarities = new Map(localPolarities);
    newKeys.forEach(key => {
      if (!newPolarities.has(key)) {
        newPolarities.set(key, 'negative');
      }
    });

    // Remove polarities for deselected items
    localPolarities.forEach((_, key) => {
      if (!newKeys.has(key)) {
        newPolarities.delete(key);
      }
    });

    setLocalSelectedKeys(newKeys);
    setLocalPolarities(newPolarities);
  };

  /**
   * Handle polarity toggle for a confluence
   */
  const handlePolarityToggle = (confluenceId: string, polarity: 'positive' | 'negative'): void => {
    const newPolarities = new Map(localPolarities);
    newPolarities.set(confluenceId, polarity);
    setLocalPolarities(newPolarities);
  };

  /**
   * Get polarity for a confluence
   */
  const getPolarity = (confluenceId: string): 'positive' | 'negative' => {
    return localPolarities.get(confluenceId) || 'negative';
  };

  /**
   * Handle opening add confluence modal
   */
  const handleAddNew = (): void => {
    setEditingConfluence(undefined);
    setShowConfluenceModal(true);
  };

  /**
   * Handle opening edit confluence modal
   */
  const handleEdit = (confluence: ConfluenceDraft): void => {
    setEditingConfluence(confluence);
    setShowConfluenceModal(true);
  };

  /**
   * Handle saving confluence
   */
  const handleSaveConfluence = (
    confluenceData: { name: string; description?: string; categoryId?: string }
  ): string => {
    if (editingConfluence) {
      // Update existing confluence
      onUpdateConfluence(editingConfluence.tempId, confluenceData);
      return editingConfluence.tempId;
    } else {
      // Add new confluence
      const tempId = onAddConfluence(confluenceData);
      // Auto-select the newly created item with default polarity
      const updatedSelection = new Set(localSelectedKeys);
      updatedSelection.add(tempId);
      setLocalSelectedKeys(updatedSelection);

      const updatedPolarities = new Map(localPolarities);
      updatedPolarities.set(tempId, 'negative');
      setLocalPolarities(updatedPolarities);

      return tempId;
    }
  };

  /**
   * Handle opening delete confirmation
   */
  const handleDeleteClick = (tempId: string): void => {
    setDeletingConfluenceId(tempId);
  };

  /**
   * Handle apply selection
   */
  const handleApply = (): void => {
    // Build confluence links with polarity
    const confluenceLinks: PlaybookConfluenceLinkDraft[] = Array.from(localSelectedKeys).map(confluenceId => ({
      confluenceId,
      polarity: getPolarity(confluenceId)
    }));

    onSelectionChange(confluenceLinks);
    onClose();
  };

  /**
   * Handle cancel
   */
  const handleCancel = (): void => {
    // Reset to original selection
    setLocalSelectedKeys(new Set(selectedConfluences.map(c => c.confluenceId)));
    setLocalPolarities(new Map(selectedConfluences.map(c => [c.confluenceId, c.polarity])));
    onClose();
  };

  /**
   * Group confluences by category
   */
  const groupedConfluences = useMemo(() => {
    const grouped = new Map<string, ConfluenceDraft[]>();
    const uncategorized: ConfluenceDraft[] = [];

    confluences.forEach((confluence) => {
      if (confluence.categoryId) {
        const existing = grouped.get(confluence.categoryId) || [];
        grouped.set(confluence.categoryId, [...existing, confluence]);
      } else {
        uncategorized.push(confluence);
      }
    });

    return { grouped, uncategorized };
  }, [confluences]);

  /**
   * Get category by ID
   */
  const getCategoryById = (categoryId: string): MarketConditionCategoryDraft | undefined => {
    return marketConditionCategories.find((cat) => cat.tempId === categoryId);
  };

  /**
   * Render a confluence item with polarity toggle if selected
   */
  const renderConfluenceItem = (confluence: ConfluenceDraft) => {
    const isSelected = localSelectedKeys.has(confluence.tempId);
    const polarity = getPolarity(confluence.tempId);

    return (
      <ListboxItem
        key={confluence.tempId}
        textValue={confluence.name}
        classNames={{
          base: "py-2",
          selectedIcon: "text-primary scale-150 order-first"
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 flex items-center gap-2">
            <div className="flex-1">
              <div className="font-medium flex items-center gap-2">
                {confluence.name}
                {isSelected && (
                  <Chip
                    size="sm"
                    color={polarity === 'positive' ? 'success' : 'warning'}
                    variant="flat"
                  >
                    {polarity === 'positive' ? 'Positive' : 'Negative'}
                  </Chip>
                )}
              </div>
              {confluence.description && (
                <div className="text-xs text-text-secondary mt-0.5">
                  {confluence.description}
                </div>
              )}
            </div>
            {isSelected && (
              <div onClick={(e) => e.stopPropagation()}>
                <PolarityToggle
                  polarity={polarity}
                  onToggle={(p) => handlePolarityToggle(confluence.tempId, p)}
                />
              </div>
            )}
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown>
              <DropdownTrigger>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label="Confluence actions"
                onAction={(key) => {
                  if (key === 'edit') {
                    handleEdit(confluence);
                  } else if (key === 'delete') {
                    handleDeleteClick(confluence.tempId);
                  }
                }}
              >
                <DropdownItem key="edit" startContent={<Pencil className="w-4 h-4" />}>
                  Edit
                </DropdownItem>
                <DropdownItem
                  key="delete"
                  color="danger"
                  startContent={<Trash2 className="w-4 h-4" />}
                >
                  Delete
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      </ListboxItem>
    );
  };

  const hasItems = confluences.length > 0;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onOpenChange={onClose}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <span>Select Confluences</span>
                <span className="text-sm font-normal text-text-secondary">
                  Select confluences and set their polarity (positive supports trade, negative argues against)
                </span>
              </ModalHeader>

              <ModalBody>
                {!hasItems ? (
                  <EmptyState onAddNew={handleAddNew} />
                ) : (
                  <Listbox
                    aria-label="Confluences"
                    selectionMode="multiple"
                    selectedKeys={localSelectedKeys}
                    onSelectionChange={(keys) => handleSelectionChange(keys as Set<string>)}
                    variant="light"
                    classNames={{
                      base: "max-h-[400px] overflow-auto"
                    }}
                  >
                    {[
                      // Categorized confluences
                      ...Array.from(groupedConfluences.grouped.entries())
                        .sort(([aId], [bId]) => {
                          const catA = getCategoryById(aId);
                          const catB = getCategoryById(bId);
                          return (catA?.order || 0) - (catB?.order || 0);
                        })
                        .map(([categoryId, categoryConfluences]) => {
                          const category = getCategoryById(categoryId);
                          if (!category) return null;

                          return (
                            <ListboxSection
                              key={categoryId}
                              title={category.name}
                              classNames={{
                                heading: "text-sm font-semibold text-primary flex w-full border-b border-default-200",
                                base: "border border-default-200"
                              }}
                            >
                              {categoryConfluences.map(renderConfluenceItem)}
                            </ListboxSection>
                          );
                        })
                        .filter((section): section is React.ReactElement => section !== null),

                      // Uncategorized confluences
                      ...(groupedConfluences.uncategorized.length > 0
                        ? [
                            <ListboxSection
                              key="uncategorized"
                              title="Uncategorized"
                              classNames={{
                                heading: "text-sm font-semibold text-text-secondary"
                              }}
                            >
                              {groupedConfluences.uncategorized.map(renderConfluenceItem)}
                            </ListboxSection>
                          ]
                        : [])
                    ]}
                  </Listbox>
                )}
              </ModalBody>

              <ModalFooter className="flex justify-between">
                <Button
                  variant="bordered"
                  color="primary"
                  startContent={<Plus className="w-4 h-4" />}
                  onPress={handleAddNew}
                >
                  Confluence
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="light"
                    onPress={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    onPress={handleApply}
                  >
                    Save
                  </Button>
                </div>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Confluence Modal */}
      <ConfluenceModal
        isOpen={showConfluenceModal}
        mode={editingConfluence ? 'edit' : 'add'}
        existingData={editingConfluence}
        marketConditionCategories={marketConditionCategories}
        onClose={() => {
          setShowConfluenceModal(false);
          setEditingConfluence(undefined);
        }}
        onSave={handleSaveConfluence}
        onCreateCategory={onCreateCategory}
      />

      {/* Delete Confirmation Modal */}
      {(() => {
        if (!deletingConfluenceId) return null;

        const confluence = confluences.find(c => c.tempId === deletingConfluenceId);
        if (!confluence) return null;

        return (
          <DeleteConfluenceConfirmationModal
            isOpen={true}
            confluence={confluence}
            currentPlaybookId={currentPlaybookId}
            playbooks={playbooks}
            isSelectedInCurrentPlaybook={localSelectedKeys.has(deletingConfluenceId)}
            onClose={() => setDeletingConfluenceId(undefined)}
            onUnselect={() => {
              // Remove from current playbook selection only
              const updatedSelection = new Set(localSelectedKeys);
              updatedSelection.delete(deletingConfluenceId);
              setLocalSelectedKeys(updatedSelection);

              const updatedPolarities = new Map(localPolarities);
              updatedPolarities.delete(deletingConfluenceId);
              setLocalPolarities(updatedPolarities);
            }}
            onDelete={() => {
              // Remove confluence entirely
              onRemoveConfluence(deletingConfluenceId);
              // Also remove from local selection if selected
              const updatedSelection = new Set(localSelectedKeys);
              updatedSelection.delete(deletingConfluenceId);
              setLocalSelectedKeys(updatedSelection);

              const updatedPolarities = new Map(localPolarities);
              updatedPolarities.delete(deletingConfluenceId);
              setLocalPolarities(updatedPolarities);
            }}
          />
        );
      })()}
    </>
  );
};
